import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { track } from "@/lib/api";

function useGo() {
  const navigate = useNavigate();
  return (cta) => {
    if (!cta) return;
    track(cta.tracking_event || "cta_click", {
      destination: cta.destination_url, text: cta.button_text,
    });
    const url = cta.destination_url || "/signup";
    if (/^https?:\/\//.test(url)) window.location.href = url;
    else navigate(url);
  };
}

/** Reusable commercial CTA block. Defaults: Start Free Trial / See Sample Report. */
export function CtaBlock({ primary, secondary, heading = "Ready to see your AI return?" }) {
  const go = useGo();
  if (!primary && !secondary) return null;
  return (
    <div
      className="my-10 rounded-2xl border border-editorial/20 bg-editorial/5 p-8 text-center"
      data-testid="resource-cta-block"
    >
      {heading ? <h3 className="font-heading text-2xl font-semibold text-navy">{heading}</h3> : null}
      {primary?.supporting_text ? (
        <p className="mx-auto mt-2 max-w-xl text-sm text-slate-600">{primary.supporting_text}</p>
      ) : null}
      <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
        {primary ? (
          <Button
            className="bg-editorial hover:bg-editorial/90"
            onClick={() => go(primary)}
            data-testid="resource-primary-cta"
          >
            {primary.button_text}
          </Button>
        ) : null}
        {secondary ? (
          <Button
            variant="outline"
            onClick={() => go(secondary)}
            data-testid="resource-secondary-cta"
          >
            {secondary.button_text}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

/** Inline primary CTA button (used inside article flow). */
export function InlineCta({ cta }) {
  const go = useGo();
  if (!cta) return null;
  return (
    <Button className="bg-editorial hover:bg-editorial/90" onClick={() => go(cta)} data-testid="resource-inline-cta">
      {cta.button_text}
    </Button>
  );
}

/** Compact horizontal CTA banner for above-the-fold + periodic in-article placement (CRO). */
export function InlineCtaBanner({ primary, secondary, label, testid = "resource-cta-inline" }) {
  const go = useGo();
  if (!primary) return null;
  return (
    <div
      className="my-8 flex flex-col items-start gap-3 rounded-xl border border-editorial/20 bg-editorial/5 px-5 py-4 sm:flex-row sm:items-center sm:justify-between"
      data-testid={testid}
    >
      <p className="text-sm font-medium text-navy">
        {label || primary.supporting_text || "See your real AI return — measured, not estimated."}
      </p>
      <div className="flex shrink-0 flex-wrap gap-2">
        <Button className="bg-editorial hover:bg-editorial/90" onClick={() => go(primary)} data-testid={`${testid}-primary`}>
          {primary.button_text}
        </Button>
        {secondary ? (
          <Button variant="outline" onClick={() => go(secondary)} data-testid={`${testid}-secondary`}>
            {secondary.button_text}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
